import { Link } from "react-router-dom"
import { Keyboard, Mail } from "lucide-react"
import { PageHeader, Section, RuledList } from "@/components/page"
import { Button } from "@/components/ui/button"
import { useAuthStore } from "../stores/authStore"
import { OPEN_SHORTCUTS_EVENT } from "../hooks/useKeyboardShortcuts"

const SUPPORT_EMAIL = "info@satelab.org"

const linkClass = "font-semibold text-primary hover:underline"
const primaryButton =
  "motion-press inline-flex min-h-11 items-center gap-2 rounded-lg bg-primary px-5 text-sm font-bold text-primary-foreground transition-[filter,transform] hover:brightness-110 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50"

function supportHref(subject: string, email?: string, extra?: string) {
  const body = [`Mi cuenta: ${email ?? ""}`, extra, "Qué pasó:"].filter((line) => line !== undefined).join("\n")
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

interface HelpTopic {
  title: string
  body: React.ReactNode
  /** Solo se muestra a las sesiones de grupo. */
  groupOnly?: boolean
}

// Una fila por lo que le pasa al estudiante, escrita en su idioma: respuesta corta y una acción.
function buildTopics(email: string | undefined, isGroup: boolean): HelpTopic[] {
  return [
    {
      title: "No puedo entrar a mi curso",
      body: (
        <>
          <p>
            Si estudias con tu institución (tu semillero), tu acceso depende de los cursos y las fechas que ella te
            asignó. Fuera de esa ventana el contenido aparece bloqueado y la lista del curso dice el motivo.
          </p>
          <p>
            Si crees que tu acceso debería estar activo, consúltalo con tu institución o{" "}
            <a href={supportHref("No puedo entrar a mi curso", email, "Curso:")} className={linkClass}>escríbenos</a>.
          </p>
        </>
      ),
    },
    {
      title: "Mi pago sigue pendiente",
      body: (
        <>
          <p>
            Un pago pendiente puede tardar unos minutos en confirmarse. La pantalla de resultado lo revisa sola y
            después te ofrece «Verificar de nuevo». Si fue rechazado, intenta de nuevo o con otro medio de pago.
          </p>
          <p>
            Si sigue pendiente o te descontaron y no tienes acceso,{" "}
            <a href={supportHref("Pago pendiente", email, "Referencia del pago:")} className={linkClass}>escríbenos con la referencia</a>{" "}
            de la pantalla de resultado.
          </p>
        </>
      ),
    },
    {
      title: "No me llegó mi certificado",
      body: (
        <>
          <p>
            El certificado se emite cuando completas todas las lecciones y apruebas los exámenes obligatorios del curso.
            Cada uno tiene un código y una página pública para verificarlo.
          </p>
          <p>
            <Link to={isGroup ? "/grupo/certificados" : "/certificados"} className={linkClass}>
              Ver mis certificados
            </Link>
            . Si completaste el curso y no lo ves,{" "}
            <a href={supportHref("No me llegó mi certificado", email, "Curso:")} className={linkClass}>escríbenos</a>.
          </p>
        </>
      ),
    },
    {
      title: "Cómo empiezo o retomo un curso",
      body: (
        <p>
          En <Link to="/dashboard" className={linkClass}>Inicio</Link> eliges un curso y, si es gratuito, te inscribes;
          si tiene precio, pagas con Wompi. Para retomar donde quedaste usa el botón «Continuar» en Inicio. Tus cursos
          están en <Link to="/mis-cursos" className={linkClass}>Mis cursos</Link>.
        </p>
      ),
    },
    {
      title: "Cambiar mi contraseña",
      body: (
        <p>
          Abre el menú de tu cuenta, abajo en la barra lateral, y elige «Configuración». Si entraste con una contraseña
          temporal, la plataforma te pedirá cambiarla.
        </p>
      ),
    },
    {
      title: "Reuniones con expertos",
      groupOnly: true,
      body: (
        <>
          <p>
            En el panel de tu grupo agendas reuniones con expertos desde un calendario integrado. El cupo se confirma
            cuando la reunión queda realmente agendada, y si cancelas puedes liberarlo desde el mismo panel.
          </p>
          <p>
            Un <strong className="font-semibold text-foreground">cupo</strong> es una reunión que tu grupo todavía puede
            agendar. Si el botón dice «Sin cupo disponible», escríbenos para consultar o pedir más.
          </p>
        </>
      ),
    },
  ].filter((topic) => !topic.groupOnly || isGroup)
}

function TopicRow({ topic }: { topic: HelpTopic }) {
  return (
    <li>
      <details className="group">
        <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 rounded-sm py-3 text-base font-medium text-foreground focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/50 [&::-webkit-details-marker]:hidden">
          {topic.title}
          <span
            aria-hidden="true"
            className="text-lg leading-none text-muted-foreground transition-transform group-open:rotate-45 motion-reduce:transition-none"
          >
            +
          </span>
        </summary>
        <div className="max-w-prose space-y-2 pb-4 text-sm leading-relaxed text-muted-foreground group-open:[animation:motion-fade-kf_var(--dur-base)_var(--ease-out-expo)_both] motion-reduce:group-open:[animation:none]">
          {topic.body}
        </div>
      </details>
    </li>
  )
}

export default function HelpPage() {
  const user = useAuthStore((s) => s.user)
  const sessionType = useAuthStore((s) => s.sessionType)
  const topics = buildTopics(user?.email, sessionType === "group")
  // En pantallas muy anchas las respuestas van en dos columnas; en el resto, una sola lista.
  const half = Math.ceil(topics.length / 2)

  return (
    <div className="space-y-10">
      <PageHeader
        title="Ayuda"
        description="Elige lo que te pasa y te decimos qué hacer."
        actions={
          <Button
            variant="outline"
            className="hidden min-h-11 sm:inline-flex sm:min-h-8"
            onClick={() => window.dispatchEvent(new Event(OPEN_SHORTCUTS_EVENT))}
          >
            <Keyboard className="h-4 w-4" aria-hidden="true" />
            Atajos de teclado
          </Button>
        }
      />

      <div className="grid gap-x-16 gap-y-10 xl:grid-cols-[22rem_minmax(0,1fr)]">
        <Section titleId="soporte" title="¿Sigues atascado?" className="xl:sticky xl:top-20 xl:self-start">
          <p className="max-w-prose text-sm text-muted-foreground">
            Escríbenos y revisamos tu caso. El mensaje ya lleva tu cuenta; solo cuéntanos qué pasó.
          </p>
          <div className="mt-4">
            <a href={supportHref("Ayuda con SateLab Academy", user?.email)} className={primaryButton}>
              <Mail className="h-4 w-4" aria-hidden="true" />
              Escribir a soporte
            </a>
          </div>
        </Section>

        <Section titleId="que-te-pasa" title="¿Qué te pasa?">
          <div className="grid gap-x-10 2xl:grid-cols-2">
            <RuledList>
              {topics.slice(0, half).map((topic) => (
                <TopicRow key={topic.title} topic={topic} />
              ))}
            </RuledList>
            <RuledList className="-mt-px 2xl:mt-0">
              {topics.slice(half).map((topic) => (
                <TopicRow key={topic.title} topic={topic} />
              ))}
            </RuledList>
          </div>
        </Section>
      </div>
    </div>
  )
}
